import { useState } from 'react';
import { motion } from 'framer-motion';
import { Send, Copy, Check, Loader2, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { SectionHeader } from './VisualAtoms';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PANEL_STYLE = {
  borderColor: '#1a1a1a',
  background: '#0d0d0d',
};

const FORM_TYPES = [
  {
    key: 'short',
    label: 'Short Form',
    mode: 'assets_only',
    helper: 'Profile already known; collect PFP and works.',
  },
  {
    key: 'long',
    label: 'Long Form',
    mode: 'full',
    helper: 'Collect full artist profile and works.',
  },
];

function GreenButton({ children, icon: Icon, onClick, disabled, loading, testId, variant = 'solid' }) {
  const base =
    'inline-flex items-center justify-center gap-2 px-3 py-2.5 text-[11px] font-mono font-medium uppercase tracking-[0.14em] rounded-none transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
  const solid =
    'bg-[#7ed4a0] text-black hover:bg-[#92dcae] hover:shadow-[0_0_18px_rgba(126,212,160,0.45)] active:scale-[0.98]';
  // "outline" is now cyan-toned (neutral admin look) so Send Request is the only green accent.
  const outline =
    'bg-transparent text-[#90E0EF] border border-[#90E0EF]/40 hover:bg-[#90E0EF]/10 hover:border-[#90E0EF] hover:shadow-[0_0_14px_rgba(144,224,239,0.25)] active:scale-[0.98]';
  const cls = `${base} ${variant === 'solid' ? solid : outline}`;
  const ResolvedIcon = loading ? Loader2 : Icon;
  return (
    <motion.button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      data-testid={testId}
      data-clickable="true"
      whileHover={!(disabled || loading) ? { y: -1.5 } : undefined}
      whileTap={!(disabled || loading) ? { scale: 0.97 } : undefined}
      className={cls}
      style={{ cursor: disabled || loading ? 'wait' : 'pointer' }}
    >
      {ResolvedIcon && <ResolvedIcon className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />}
      {children}
    </motion.button>
  );
}

function FormTypePill({ active, item, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-clickable="true"
      data-testid={`form-type-${item.key}`}
      className={`text-left p-3 rounded-none transition-all border ${
        active
          ? 'border-[#90E0EF] bg-[#90E0EF]/5 shadow-[0_0_10px_rgba(144,224,239,0.18)]'
          : 'border-[#222] bg-[#0a0a0a] hover:border-[#333]'
      }`}
      style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
    >
      <p className={`text-[11px] font-mono font-medium uppercase tracking-[0.14em] ${active ? 'text-[#90E0EF]' : 'text-[#aaa]'}`}>
        {item.label}
      </p>
      <p className="text-[10px] font-mono text-[#666] mt-1 leading-snug">
        {item.helper}
      </p>
    </button>
  );
}

function StatusLine({ label, value }) {
  if (!value) return null;
  let formatted = value;
  try { formatted = new Date(value).toLocaleString(); } catch { /* keep raw */ }
  return (
    <div className="flex items-center justify-between gap-3 text-[10px] font-mono">
      <span className="text-[#666] uppercase tracking-[0.14em]">{label}</span>
      <span className="text-[#aaa]">{formatted}</span>
    </div>
  );
}

/**
 * Admin panel for the Artist Profile page.
 *
 * Lets the curator pick Short / Long form, then either
 *   - Send Request (email) or
 *   - Copy Form Link (silent)
 *
 * Props:
 *   artistId           — required
 *   exhibitionId       — required (selected admin exhibition)
 *   openCallApplicationId — optional
 *   assetForm          — { sent_at, opened_at, submitted_at } from artist_profiles
 *   onLinkGenerated    — callback so parent can refresh artist data
 */
export default function ArtistIntakePanel({
  artistId,
  exhibitionId,
  openCallApplicationId,
  assetForm = {},
  defaultFormType = 'short',
  onLinkGenerated,
}) {
  const [formType, setFormType] = useState(defaultFormType);
  const [sending, setSending] = useState(false);
  const [copying, setCopying] = useState(false);
  const [link, setLink] = useState('');
  const [linkMode, setLinkMode] = useState('');
  const [copied, setCopied] = useState(false);

  const selected = FORM_TYPES.find((f) => f.key === formType) || FORM_TYPES[0];
  const exhibitionMissing = !exhibitionId;

  const generate = async ({ sendEmail }) => {
    if (exhibitionMissing) {
      toast.error('Select an exhibition first');
      return null;
    }
    const body = {
      exhibition_id: exhibitionId,
      send_email: sendEmail,
      mode: selected.mode,
    };
    if (openCallApplicationId) body.open_call_application_id = openCallApplicationId;
    body.frontend_origin = window.location.origin;
    const res = await axios.post(`${API}/admin/artists/${artistId}/intake-link`, body);
    setLink(res.data.link);
    setLinkMode(res.data.intake_mode || res.data.mode);
    return res.data;
  };

  // Same as generate but doesn't notify parent (used by Copy to avoid re-mount).
  const generateSilent = generate;

  const handleSend = async () => {
    setSending(true);
    try {
      const data = await generate({ sendEmail: true });
      if (!data) return;
      if (data.email_sent) {
        toast.success(`${selected.label} link sent via email.`);
      } else if (data.email_error) {
        toast.error(`Email failed: ${data.email_error}`);
      } else {
        toast.success(`${selected.label} link generated.`);
      }
      // Refresh parent only after a real send (asset_form_sent_at changes).
      onLinkGenerated?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to send request.');
    } finally {
      setSending(false);
    }
  };

  const handleCopy = async () => {
    setCopying(true);
    try {
      // Silent: do NOT trigger parent reload — copy doesn't change artist state.
      const data = await generateSilent({ sendEmail: false });
      if (!data) return;
      try {
        await navigator.clipboard.writeText(data.link);
        toast.success(`${selected.label} link copied.`);
        setCopied(true);
        setTimeout(() => setCopied(false), 1400);
      } catch {
        toast.message('Link generated. Copy manually below.');
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to generate link.');
    } finally {
      setCopying(false);
    }
  };

  const copyExisting = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      toast.success('Link copied');
      setTimeout(() => setCopied(false), 1400);
    } catch {
      toast.error('Could not copy');
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="border p-5 sm:p-6"
      style={PANEL_STYLE}
      data-testid="artist-intake-panel"
    >
      <SectionHeader
        icon={Sparkles}
        title="Artist Intake"
        helper="Send or copy a private form link for this artist."
      />

      {/* Form type selector */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-5">
        {FORM_TYPES.map((item) => (
          <FormTypePill
            key={item.key}
            item={item}
            active={formType === item.key}
            onClick={() => setFormType(item.key)}
            disabled={sending || copying}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2">
        <GreenButton
          icon={Send}
          onClick={handleSend}
          disabled={sending || copying || exhibitionMissing}
          loading={sending}
          testId="artist-intake-send-request-btn"
          variant="solid"
        >
          {sending ? 'Sending…' : 'Send Request'}
        </GreenButton>
        <GreenButton
          icon={Copy}
          onClick={handleCopy}
          disabled={sending || copying || exhibitionMissing}
          loading={copying}
          testId="artist-intake-copy-link-btn"
          variant="outline"
        >
          {copying ? 'Generating…' : 'Copy Form Link'}
        </GreenButton>
      </div>

      {exhibitionMissing && (
        <p className="mt-3 text-[10px] font-mono text-amber-300/80">
          Choose an exhibition in the topbar before generating a link.
        </p>
      )}

      {/* Generated link */}
      {link && (
        <div
          className="mt-4 border border-[#222] bg-[#0a0a0a] p-2.5 flex items-center gap-2"
          data-testid="artist-intake-link-display"
        >
          <div className="min-w-0 flex-1">
            <p className="text-[9px] font-mono text-[#666] uppercase tracking-[0.18em]">
              {linkMode === 'full' ? 'Long Form' : 'Short Form'} link
            </p>
            <p className="text-[10px] font-mono text-[#aaa] truncate" title={link}>
              {link}
            </p>
          </div>
          <button
            type="button"
            onClick={copyExisting}
            data-clickable="true"
            data-testid="artist-intake-link-copy-btn"
            aria-label="Copy link"
            className="w-7 h-7 border border-[#222] flex items-center justify-center text-[#90E0EF] hover:bg-[#90E0EF]/10 hover:border-[#90E0EF]/40 transition-colors shrink-0"
          >
            {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          </button>
        </div>
      )}

      {/* Lifecycle status */}
      {(assetForm.sent_at || assetForm.opened_at || assetForm.submitted_at) && (
        <div className="mt-5 pt-4 border-t border-[#1a1a1a] space-y-1.5">
          <StatusLine label="Last sent" value={assetForm.sent_at} />
          <StatusLine label="Last opened" value={assetForm.opened_at} />
          <StatusLine label="Last submitted" value={assetForm.submitted_at} />
        </div>
      )}
    </motion.div>
  );
}
